import { renderMarkdown } from '../markdown'

// How long new text takes to fade in.
const fadeMs = 400

const template = document.createElement('template')
template.innerHTML = `
  <style>
    :host {
      display: block;
      /* Markdown is laid out by its own elements, not the log's pre-wrap. */
      white-space: normal;
    }
    .fade {
      animation: fade-in ${fadeMs}ms ease-out both;
    }
    @keyframes fade-in {
      from { opacity: 0; }
    }
    @media (prefers-reduced-motion: reduce) {
      .fade { animation: none; }
    }
    #content > :first-child { margin-top: 0; }
    #content > :last-child { margin-bottom: 0; }
    :is(p, ul, ol, pre, blockquote, table) {
      margin: var(--space-2) 0;
    }
    :is(h1, h2, h3, h4, h5, h6) {
      margin: var(--space-3) 0 var(--space-2);
      font-size: 1em;
      font-weight: var(--font-weight-bold);
    }
    h1 { font-size: 1.25em; }
    h2 { font-size: 1.125em; }
    :is(ul, ol) {
      padding-left: var(--space-4);
    }
    a {
      color: var(--color-accent-strong);
    }
    code {
      padding: 0.1em 0.3em;
      border-radius: var(--radius-sm);
      background: var(--color-card);
      font-family: var(--font-mono);
      font-size: var(--font-size-xs);
    }
    pre {
      padding: var(--space-2) var(--space-2-5);
      border: var(--border-width) solid var(--color-border);
      border-radius: var(--radius-sm);
      background: var(--color-card);
      overflow-x: auto;
    }
    pre code {
      padding: 0;
      background: none;
    }
    blockquote {
      margin-left: 0;
      padding-left: var(--space-2);
      border-left: var(--rule-width) solid var(--color-border-strong);
      color: var(--color-text-muted);
    }
    table {
      border-collapse: collapse;
    }
    :is(th, td) {
      padding: var(--space-1) var(--space-2);
      border: var(--border-width) solid var(--color-border);
    }
    hr {
      border: none;
      border-top: var(--border-width) solid var(--color-border);
    }
  </style>
  <div id="content"></div>
`

// Text that appeared at `time`, as offsets into the rendered text.
interface Arrival {
  start: number
  end: number
  time: number
}

/**
 * Something an agent said, rendered as markdown. It can be streamed in with
 * `addText()`, and each new piece fades in as it arrives.
 */
export class AgentReply extends HTMLElement {
  #root: ShadowRoot
  #content: HTMLElement
  #text = ''
  // How much rendered text has been shown, and when the latest of it came.
  #shown = 0
  #arrivals: Arrival[] = []

  constructor() {
    super()
    this.#root = this.attachShadow({ mode: 'open' })
    this.#root.append(template.content.cloneNode(true))
    this.#content = this.#root.getElementById('content')!
  }

  /** Adds the next piece of the reply. */
  addText(text: string) {
    this.setText(this.#text + text)
  }

  /** Sets the whole reply, fading in whatever is new. */
  setText(text: string) {
    if (text === this.#text) return
    this.#text = text
    this.#render()
  }

  // The markdown is rendered afresh each time, since new text can change how
  // earlier text renders. Text that's still fading in is given a negative
  // delay so its fade carries on where it was rather than starting over.
  #render() {
    const fragment = renderMarkdown(this.#text)
    const now = performance.now()
    const length = fragment.textContent?.length ?? 0
    // New text can make earlier text render shorter, as when a closing `**`
    // turns `**bold` into bold. Cutting arrivals back to the end keeps them
    // apart and in order, which fadeIn relies on.
    for (const arrival of this.#arrivals) arrival.end = Math.min(arrival.end, length)
    if (length > this.#shown) {
      this.#arrivals.push({ start: this.#shown, end: length, time: now })
    }
    this.#shown = length
    this.#arrivals = this.#arrivals.filter(a => a.start < a.end && now - a.time < fadeMs)

    fadeIn(fragment, this.#arrivals, now)
    this.#content.replaceChildren(fragment)
  }
}

// Wraps the text each arrival covers in a span that fades in from its time.
function fadeIn(root: Node, arrivals: Arrival[], now: number) {
  if (arrivals.length === 0) return
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT)
  const nodes: Text[] = []
  while (walker.nextNode()) nodes.push(walker.currentNode as Text)

  let offset = 0
  for (const node of nodes) {
    const nodeStart = offset
    offset += node.length
    // Whitespace between blocks isn't seen, and spans don't belong in tables and lists.
    if (node.data.trim() === '') continue

    // Split from the end, so the start of the node stays where it was.
    for (const arrival of arrivals.slice().reverse()) {
      const start = Math.max(arrival.start, nodeStart) - nodeStart
      const end = Math.min(arrival.end, offset) - nodeStart
      if (start >= end) continue
      const piece = node.splitText(start)
      piece.splitText(end - start)
      const span = document.createElement('span')
      span.className = 'fade'
      span.style.animationDelay = `${arrival.time - now}ms`
      piece.replaceWith(span)
      span.append(piece)
    }
  }
}

customElements.define('meat-agent-reply', AgentReply)

declare global {
  interface HTMLElementTagNameMap {
    'meat-agent-reply': AgentReply
  }
}
